import { ChatAttachmentRef } from "./types";

/** Max attachment size accepted by the server (ChatAttachmentService::MAX_BYTES). */
export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;

/** Text file types accepted as attachments, by extension (mirrors ChatAttachmentService::TEXT_TYPES). */
export const TEXT_TYPES_BY_EXTENSION: Record<string, string> = {
	csv: 'text/csv',
	txt: 'text/plain',
	json: 'application/json',
};

/**
 * Spreadsheet file types, by extension. No model provider reads a workbook natively, so a spreadsheet
 * is converted to a text attachment in the browser (spreadsheetToText) before it is sent.
 */
export const SPREADSHEET_EXTENSIONS = ['xlsx', 'xlsm', 'xls', 'ods'];

/** The `accept` attribute of the file input: images plus the text and spreadsheet file types. */
export const ACCEPTED_FILES = [
	'image/*',
	...Object.keys(TEXT_TYPES_BY_EXTENSION).map(ext => `.${ext}`),
	...SPREADSHEET_EXTENSIONS.map(ext => `.${ext}`),
].join(',');

/** Column separator of a CSV converted from a spreadsheet (the convention of Czech Excel exports). */
export const SPREADSHEET_CSV_SEPARATOR = ';';

// Pasted text becomes a file attachment instead of message text when it looks like file content:
// structured (JSON, delimited table) with at least this many lines, or anything at least this long.
export const PASTE_FILE_MIN_LINES = 20;
export const PASTE_FILE_MIN_CHARS = 5000;

export function isImageAttachment(att: ChatAttachmentRef): boolean {
	return (att.media_type ?? '').startsWith('image/');
}

export function attachmentIcon(att: ChatAttachmentRef): string {
	switch (att.media_type) {
		case 'text/csv': return 'file-csv';
		case 'application/json': return 'file-code';
		case 'text/plain': return 'file-lines';
		default: return 'file';
	}
}

function extensionOf(fileName: string): string {
	return fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
}

/** Text file type by the file's extension — the browser's MIME type is unreliable (Windows reports .csv as application/vnd.ms-excel). */
export function textTypeOf(file: File): string | null {
	return TEXT_TYPES_BY_EXTENSION[extensionOf(file.name)] ?? null;
}

/** Whether the file is a spreadsheet to convert (by extension: Windows reports .xls and .csv under the same MIME type). */
export function isSpreadsheetFile(file: File): boolean {
	return SPREADSHEET_EXTENSIONS.includes(extensionOf(file.name));
}

/**
 * The part of SheetJS the conversion uses. The full library is not bundled: @omegagrid/plugin-xlsx
 * loads it on demand (`loader.get()`), tests inject a stand-in.
 */
export interface SpreadsheetReader {
	read(data: Uint8Array, options: { type: 'array' }): { SheetNames: string[]; Sheets: Record<string, unknown> };
	utils: { sheet_to_csv(sheet: unknown, options?: { FS?: string; blankrows?: boolean }): string };
}

export interface ConvertedSpreadsheet {
	text: string;
	fileName: string;
	mediaType: string;
}

/**
 * Converts a workbook to the content of a text attachment, values as formatted in the spreadsheet
 * (dates, thousands, decimals as the user sees them). One sheet with data becomes a semicolon-separated
 * CSV named after the file; several sheets become one plain-text file with a `=== Sheet: name ===`
 * heading per sheet. Sheets without data are skipped; null when the whole workbook is empty.
 */
export function spreadsheetToText(bytes: Uint8Array, XLSX: SpreadsheetReader, fileName: string): ConvertedSpreadsheet | null {
	const workbook = XLSX.read(bytes, { type: 'array' });
	const sheets = workbook.SheetNames
		.map(name => ({
			name,
			csv: XLSX.utils.sheet_to_csv(workbook.Sheets[name], { FS: SPREADSHEET_CSV_SEPARATOR, blankrows: false }).trim(),
		}))
		.filter(sheet => sheet.csv !== '');
	if (!sheets.length) return null;

	const baseName = fileName.replace(/\.[^.]+$/, '') || 'sheet';
	if (sheets.length === 1) {
		return { text: sheets[0].csv + '\n', fileName: `${baseName}.csv`, mediaType: 'text/csv' };
	}
	return {
		text: sheets.map(sheet => `=== Sheet: ${sheet.name} ===\n${sheet.csv}\n`).join('\n'),
		fileName: `${baseName}.txt`,
		mediaType: 'text/plain',
	};
}

/** Decodes a text file: BOM-detected UTF-8 / UTF-16, otherwise UTF-8 with a Windows-1250 fallback (Czech Excel exports). */
export async function readTextFile(file: File): Promise<string> {
	const bytes = new Uint8Array(await file.arrayBuffer());
	if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) return new TextDecoder('utf-8').decode(bytes.subarray(3));
	if (bytes[0] === 0xFF && bytes[1] === 0xFE) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
	if (bytes[0] === 0xFE && bytes[1] === 0xFF) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
	try {
		return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
	} catch {
		return new TextDecoder('windows-1250').decode(bytes);
	}
}

export function encodeText(text: string): Uint8Array {
	return new TextEncoder().encode(text);
}

/** Builds a text attachment from UTF-8 bytes (stored as base64, like image data). */
export function textAttachment(bytes: Uint8Array, fileName: string, mediaType: string): ChatAttachmentRef {
	return { file_name: fileName, media_type: mediaType, data: base64(bytes) };
}

function base64(bytes: Uint8Array): string {
	let binary = '';
	const chunk = 0x8000; // String.fromCharCode has an argument-count limit
	for (let i = 0; i < bytes.length; i += chunk) {
		binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
	}
	return btoa(binary);
}

/**
 * Whether pasted text should become a file attachment, and as which type: JSON when it parses,
 * a table when most lines share a delimiter count, plain text when it is just long.
 */
export function detectPastedFile(text: string): { extension: string; mediaType: string } | null {
	const trimmed = text.trim();
	if (!trimmed) return null;
	const lines = trimmed.split(/\r?\n/);
	const long = trimmed.length >= PASTE_FILE_MIN_CHARS;
	const structured = lines.length >= PASTE_FILE_MIN_LINES;
	if ((structured || long) && isJson(trimmed)) return { extension: 'json', mediaType: 'application/json' };
	if (structured && isDelimited(lines)) return { extension: 'csv', mediaType: 'text/csv' };
	return long ? { extension: 'txt', mediaType: 'text/plain' } : null;
}

function isJson(text: string): boolean {
	if (!/^[[{]/.test(text)) return false;
	try {
		JSON.parse(text);
		return true;
	} catch {
		return false;
	}
}

// A table: one delimiter (tab, semicolon, comma) occurs the same number of times (at least once)
// on at least 90 % of the lines.
function isDelimited(lines: string[]): boolean {
	const sample = lines.slice(0, 200);
	for (const delimiter of ['\t', ';', ',']) {
		const counts = sample.map(line => line.split(delimiter).length - 1);
		const expected = counts[0];
		if (expected < 1) continue;
		const matching = counts.filter(c => c === expected).length;
		if (matching / sample.length >= 0.9) return true;
	}
	return false;
}
